import { Component, OnInit, Input, SimpleChanges } from '@angular/core';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { StatsAndReportsService } from '@admin/stats-and-reports/shared/services/stats-and-reports.service';
import {
  SalesReportModel,
  ChannelAnnouncementsReportModel,
  ProductReportModel,
  AdditionalProductsReportModel,
  PortfolioProductsReportModel,
} from '@admin/stats-and-reports/shared/models/sales-report-model';
import { SalesReportFilterModel } from '../shared/models/sales-report-filter-model';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { PortfolioModel } from '@admin/portfolios/shared/models/portfolio-model';
import { PortfolioService } from '@admin/portfolios/shared/services/portfolio.service';
import { CommonLookupItem } from '@admin/shared/models/common-lookup-item.model';
import { LookupService } from '@admin/shared/services/lookup.service';
import { ProductType } from '../../product-purchases/shared/models/product-type';
import { StaticValues } from '@admin/shared/static-values';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-sales-report',
  templateUrl: './sales-report.component.html',
  styleUrls: ['./sales-report.component.less'],
  standalone: false,
})
export class SalesReportComponent implements OnInit {
  @Input()
  public portfolioId: number;

  public filter: SalesReportFilterModel;

  public reportData: SalesReportModel;

  public cvDbReportChartData: any;
  public sliderReportChartData: any;
  public logoReportChartData: any;
  public frameReportChartData: any;
  public shareReportChartData: any;
  public announcementReporChartData: any;
  public additionalProductTableData: {
    [groupId: number]: AdditionalProductsReportModel[];
  };

  public yearFilterOptions: SelectItem[];
  public monthFilterOptions: SelectItem[];
  public dayFilterOptions: SelectItem[];
  public portfolioFilterOptions: SelectItem[];

  public providerWebsiteList: CommonLookupItem<number, string>[];

  public portfolios: PortfolioModel[];

  public loading: boolean;
  public isPortfolioStated: boolean;
  public showDetailedUpgrades: boolean;

  private chartColors: string[];

  AdditionalProductGroupEnum = AdditionalProductGroup;

  constructor(
    private lookupService: LookupService,
    private statsAndReportService: StatsAndReportsService,
    private translateService: TranslateService,
    private portfolioService: PortfolioService
  ) {
    this.reportData = new SalesReportModel();
    this.cvDbReportChartData = null;
    this.sliderReportChartData = null;
    this.logoReportChartData = null;
    this.frameReportChartData = null;
    this.shareReportChartData = null;
    this.announcementReporChartData = null;
    this.additionalProductTableData = {};
    this.providerWebsiteList = [];
    this.filter = new SalesReportFilterModel();
    this.loading = false;
    this.isPortfolioStated = false;
    this.showDetailedUpgrades = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['portfolioId'] && changes['portfolioId'].currentValue != null) {
      this.filter.portfolioId = changes['portfolioId'].currentValue;
      this.isPortfolioStated = true;
    }
  }

  onLoad(event: LazyLoadEvent) {
    this.onFilter();
  }

  onUpgradesLoad(event: LazyLoadEvent) {
    this.loadUpgrades();
  }

  onFilter() {
    this.adjustFilter();
    this.statsAndReportService.getSalesReport(this.filter).subscribe((data) => {
      this.reportData = data;
      this.sortCvDbReport();
      this.cvDbReportChartData = this.getProductChartData(
        this.reportData.cvDatabasesReportData
      );
      this.sliderReportChartData = this.getProductChartData(
        this.reportData.sliderBannersReportData
      );
      this.initAdditionalProducthartData(
        this.reportData.portfolioAdditionalProductsReportData
      );
      this.initAdditionalProductsData(
        this.reportData.additionalProductsReportData
      );
      this.announcementReporChartData = this.getPortfolioAnnouncementChartData(
        this.reportData.portfolioAnnouncementsReportData
      );
    });
  }

  adjustFilter() {
    if (this.filter.year == null || this.filter.year == -1) {
      this.filter.year = new Date().getFullYear();
      this.filter.month = null;
    }
    if (this.filter.month == -1) {
      this.filter.month = null;
    }
  }

  loadUpgrades() {
    this.adjustFilter();
    this.loading = true;
    this.statsAndReportService
      .getUpgradeSalesReport(this.filter)
      .subscribe((data) => {
        this.reportData.upgradeCombinationsReportData = data.items;
        this.loading = false;
      });
  }

  getEmptyMessageResourceName() {
    if (this.loading) return 'Loading...';

    return 'NoRecordsFound';
  }

  onClear() {
    if (!this.isPortfolioStated) this.filter.portfolioId = null;
    this.filter.year = null;
    this.filter.month = null;
    this.filter.day = null;
    this.filter.website = null;
  }

  ngOnInit() {
    let lookupData = this.lookupService.getCachedLookupData();
    this.providerWebsiteList = lookupData.providerWebsiteList;
    this.initYearFilterOptions();
    this.initMonthFilterOptions();
    this.initDayFilterOptions();
    this.loadPortfoliosDropDown();
    this.initChartColors();
  }

  public listAdditionalProductGroups() {
    return Object.keys(this.additionalProductTableData);
  }

  public getAdditionalProductsTotal(group: AdditionalProductGroup) {
    return this.additionalProductTableData[group]
      .map((r) => r.count)
      .reduce((sum, curr) => sum + curr, 0);
  }

  private initYearFilterOptions() {
    this.yearFilterOptions = [];
    let minYear = 2017;
    let currYear = new Date().getFullYear();
    for (var i = minYear; i <= currYear; i++)
      this.yearFilterOptions.push({ label: i.toString(), value: i });
  }

  private initMonthFilterOptions() {
    this.monthFilterOptions = [];
    for (var i = 1; i <= 12; i++)
      this.monthFilterOptions.push({
        label: this.translateService.instant(
          'label.' + StaticValues.monthNames[i - 1]
        ),
        value: i,
      });
  }

  private initDayFilterOptions() {
    this.dayFilterOptions = [];
    for (var i = 1; i <= 31; i++)
      this.dayFilterOptions.push({ label: i.toString(), value: i });
  }

  private initAdditionalProductsData(report: AdditionalProductsReportModel[]) {
    this.additionalProductTableData = {};

    let frames = report.filter(
      (r) =>
        r.product == 'Frame' ||
        r.product == 'Frame+' ||
        r.product == 'Flame Frame'
    );
    let shares = report.filter(
      (r) =>
        r.product == 'Facebook share' ||
        r.product == 'LinkedIn share' ||
        r.product == 'Instagram share' ||
        r.product == 'HR Boost'
    );
    let newYearProducts = report.filter(
      (r) =>
        r.product == 'Blue' ||
        r.product == 'Green	' ||
        r.product == 'Red' ||
        r.product == 'Santa' ||
        r.product == 'Tree'
    );
    let others = report.filter(
      (r) =>
        r.product == 'Anonymous' ||
        r.product == 'Logo' ||
        r.product == 'Announcement matching'
    );
    others.push(
      new AdditionalProductsReportModel('Renewal', this.reportData.renewals)
    );
    others.push(
      new AdditionalProductsReportModel(
        'Total Renewal Count',
        this.reportData.totalRenewalCount
      )
    );

    this.additionalProductTableData[AdditionalProductGroup.Frame] = frames;
    this.additionalProductTableData[AdditionalProductGroup.Share] = shares;
    this.additionalProductTableData[AdditionalProductGroup.NewYear] =
      newYearProducts;
    this.additionalProductTableData[AdditionalProductGroup.Other] = others;
  }

  private initChartColors() {
    this.chartColors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#C0C0C0',
      '#800080',
      '#008080',
      '#FF00FF',
      '#000080',
      '#00FFFF',
      '#00FF00',
      '#FFFF00',
      '#800000',
      '#008000',
      '#FF0000',
      '#000000',
      '#808080',
    ];
  }

  loadPortfoliosDropDown() {
    this.portfolioService.getPortfolios().subscribe((p) => {
      this.portfolios = p.items;
    });
  }

  private sortCvDbReport() {
    let sortOrder = [
      '3 days',
      '1 week',
      '2 week',
      '1 month',
      '3 months',
      '6 months',
      '1 year',
      '1 week(budget)',
      '2 weeks(budget)',
      '1 month(budget)',
      '3 months(budget)',
      '6 months(budget)',
      '1 year(budget)',
    ];
    this.reportData.cvDatabasesReportData.sort(function (a, b) {
      return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
    });
  }

  private getProductChartData(data: ProductReportModel[]) {
    return {
      labels: data.map((d) => d.name),
      datasets: [
        {
          label: this.translateService.instant('label.Free'),
          backgroundColor: '#42A5F5',
          borderColor: '#1E88E5',
          data: data.map((d) => d.totalFree),
        },
        {
          label: this.translateService.instant('label.Sold'),
          backgroundColor: '#9CCC65',
          borderColor: '#7CB342',
          data: data.map((d) => d.totalSold),
        },
      ],
    };
  }

  private initAdditionalProducthartData(data: PortfolioProductsReportModel[]) {
    this.logoReportChartData = this.getLogoReportChartData(
      data.filter((d) => d.productTypeId == ProductType.Announcement_Logo)
    );
    this.frameReportChartData = this.getFrameReportChartData(
      data.filter((d) => d.productTypeId == ProductType.Announcement_Frame)
    );
    this.shareReportChartData = this.getShareReportChartData(
      data.filter(
        (d) =>
          d.productTypeId == ProductType.Announcement_FacebookShare ||
          d.productTypeId == ProductType.Announcement_LinkedInShare
      )
    );
  }

  private getLogoReportChartData(
    logoReprotData: PortfolioProductsReportModel[]
  ) {
    let result = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
        },
      ],
    };
    let i;
    for (i = 0; i < this.portfolios.length; i++) {
      let p = this.portfolios[i];
      let report = logoReprotData.filter((d) => d.portfolioId == p.portfolioId);
      if (report.length > 0) {
        result.labels.push(p.name);
        result.datasets[0].data.push(report[0].totalSold);
        if (i >= this.chartColors.length)
          this.chartColors.push(this.getRandomColor());
        result.datasets[0].backgroundColor.push(this.chartColors[i]);
      }
    }

    return result;
  }

  private getShareReportChartData(data: PortfolioProductsReportModel[]) {
    let result = {
      labels: [],
      datasets: [
        {
          label: this.translateService.instant('label.Facebook'),
          backgroundColor: '#42A5F5',
          borderColor: '#1E88E5',
          data: [],
        },
        {
          label: this.translateService.instant('label.LinkedIn'),
          backgroundColor: '#9CCC65',
          borderColor: '#7CB342',
          data: [],
        },
      ],
    };

    this.portfolios.forEach((p) => {
      let report = data.filter((d) => d.portfolioId == p.portfolioId);
      let fb = report.filter(
        (r) => r.productTypeId == ProductType.Announcement_FacebookShare
      )[0];
      let ln = report.filter(
        (r) => r.productTypeId == ProductType.Announcement_LinkedInShare
      )[0];

      if (p.portfolioId === this.portfolioId || fb || ln) {
        result.labels.push(p.name);
        result.datasets[0].data.push(fb ? fb.totalSold : 0);
        result.datasets[1].data.push(ln ? ln.totalSold : 0);
      }
    });

    return result;
  }

  private getFrameReportChartData(data: PortfolioProductsReportModel[]) {
    let result = {
      labels: [],
      datasets: [
        {
          label: this.translateService.instant('label.Frame'),
          backgroundColor: '#42A5F5',
          borderColor: '#1E88E5',
          data: [],
        },
        {
          label: this.translateService.instant('label.FramePlus'),
          backgroundColor: '#9CCC65',
          borderColor: '#7CB342',
          data: [],
        },
        {
          label: this.translateService.instant('label.FlameFrame'),
          backgroundColor: '#FF0000',
          borderColor: '#7CB342',
          data: [],
        },
      ],
    };

    this.portfolios.forEach((p) => {
      let report = data.filter((d) => d.portfolioId == p.portfolioId);
      let frame = report.filter((r) => r.code === 'Frame')[0];
      let framePlus = report.filter((r) => r.code === 'Frame+')[0];
      let flameFrame = report.filter((r) => r.code === 'Flame Frame')[0];

      if (
        p.portfolioId === this.portfolioId ||
        frame ||
        framePlus ||
        flameFrame
      ) {
        result.labels.push(p.name);
        result.datasets[0].data.push(frame ? frame.totalSold : 0);
        result.datasets[1].data.push(framePlus ? framePlus.totalSold : 0);
        result.datasets[2].data.push(flameFrame ? flameFrame.totalSold : 0);
      }
    });

    return result;
  }

  private getPortfolioAnnouncementChartData(
    data: PortfolioProductsReportModel[]
  ) {
    let result = {
      labels: [],
      datasets: [
        {
          label: this.translateService.instant('label.Total'),
          backgroundColor: '#42A5F5',
          borderColor: '#1E88E5',
          data: [],
        },
        {
          label: this.translateService.instant('label.Sold'),
          backgroundColor: '#9CCC65',
          borderColor: '#7CB342',
          data: [],
        },
      ],
    };

    this.portfolios.forEach((p) => {
      let report = data.filter((d) => d.portfolioId == p.portfolioId)[0];
      if (p.portfolioId === this.portfolioId || report.total > 0) {
        result.labels.push(p.name);
        result.datasets[0].data.push(report.total);
        result.datasets[1].data.push(report.totalSold);
      }
    });

    return result;
  }

  private getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }
}

enum AdditionalProductGroup {
  Frame = 1,
  Share = 2,
  NewYear = 3,
  Other = 4,
}
